import { __decorate } from "tslib";
import { css, html, LitElement } from 'lit';
import { property, query } from 'lit/decorators.js';
import { createPopper } from '@popperjs/core';
import { startAnimations, stopAnimations } from '../../internal/animate.js';
import { emit, waitForEvent } from '../../internal/event.js';
import { watch } from '../../internal/watch.js';
import { getTabbableBoundary } from '../../internal/tabbable.js';
import { setDefaultAnimation, getAnimation } from '../../utilities/animation-registry.js';
import componentStyles from '../../styles/component.styles.js';
import { DROPDOWN_PLACEMENTS } from './constants/DropdownConstants.js';
import { ARC_EVENTS } from '../../internal/constants/eventConstants.js';
export default class ArcDropdown extends LitElement {
    constructor() {
        super(...arguments);
        this.placement = DROPDOWN_PLACEMENTS['bottom-start'];
        this.distance = 0;
        this.skidding = 0;
        this.open = false;
        this.disabled = false;
        this.hoist = false;
        this.handleTriggerKeyUp = (event) => {
            if (event.key === ' ') {
                event.preventDefault();
            }
        };
    }
    async handleOpenChange() {
        if (this.disabled) {
            return;
        }
        this.updateAccessibleTrigger();
        if (this.open) {
            emit(this, ARC_EVENTS.show);
            document.addEventListener('keydown', this.handleDocumentKeyDown);
            document.addEventListener('mousedown', this.handleDocumentMouseDown);
            await stopAnimations(this);
            this.popover.update();
            this.panel.hidden = false;
            const { keyframes, options } = getAnimation(this, 'dropdown.show');
            await startAnimations(this.panel, keyframes, options);
            emit(this, ARC_EVENTS.afterShow);
        }
        else {
            emit(this, ARC_EVENTS.hide);
            document.removeEventListener('keydown', this.handleDocumentKeyDown);
            document.removeEventListener('mousedown', this.handleDocumentMouseDown);
            await stopAnimations(this);
            const { keyframes, options } = getAnimation(this, 'dropdown.hide');
            await startAnimations(this.panel, keyframes, options);
            this.panel.hidden = true;
            emit(this, ARC_EVENTS.afterHide);
        }
    }
    handlePopoverOptionsChange() {
        if (this.popover) {
            this.popover.setOptions({
                placement: this.placement,
                strategy: this.hoist ? 'fixed' : 'absolute',
                modifiers: [
                    {
                        name: 'flip',
                        options: {
                            boundary: 'viewport',
                        },
                    },
                    {
                        name: 'offset',
                        options: {
                            offset: [this.skidding, this.distance],
                        },
                    },
                ],
            });
        }
    }
    connectedCallback() {
        super.connectedCallback();
        this.handlePanelSelect = this.handlePanelSelect.bind(this);
        this.handleDocumentKeyDown = this.handleDocumentKeyDown.bind(this);
        this.handleDocumentMouseDown = this.handleDocumentMouseDown.bind(this);
        this.updateComplete.then(() => {
            this.popover = createPopper(this.trigger, this.positioner, {
                placement: this.placement,
                strategy: this.hoist ? 'fixed' : 'absolute',
                modifiers: [
                    {
                        name: 'flip',
                        options: {
                            boundary: 'viewport',
                        },
                    },
                    {
                        name: 'offset',
                        options: {
                            offset: [this.skidding, this.distance],
                        },
                    },
                ],
            });
        });
    }
    firstUpdated() {
        this.panel.hidden = !this.open;
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.hide();
        this.popover.destroy();
    }
    focusOnTrigger() {
        const trigger = this.triggerSlot.assignedElements({ flatten: true })[0];
        if (trigger && typeof trigger.focus === 'function') {
            trigger.focus();
        }
    }
    getMenu() {
        const slot = this.panel.querySelector('slot');
        return slot.assignedElements({ flatten: true }).filter(el => el.tagName === 'ARC-MENU')[0];
    }
    handleDocumentKeyDown(event) {
        var _a;
        if (event.key === 'Escape') {
            this.hide();
            this.focusOnTrigger();
            return;
        }
        if (event.key === 'Tab') {
            if (this.open && ((_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.tagName) === 'ARC-MENU-ITEM') {
                event.preventDefault();
                this.hide();
                this.focusOnTrigger();
            }
        }
    }
    handleDocumentMouseDown(event) {
        const path = event.composedPath();
        if (!path.includes(this)) {
            this.hide();
        }
    }
    handleTriggerKeyDown(event) {
        const menu = this.getMenu();
        const menuItems = menu ? [...menu.querySelectorAll('arc-menu-item')] : [];
        const firstMenuItem = menuItems[0];
        const lastMenuItem = menuItems[menuItems.length - 1];
        if (event.key === 'Escape') {
            this.focusOnTrigger();
            this.hide();
            return;
        }
        if ([' ', 'Enter'].includes(event.key)) {
            event.preventDefault();
            this.handleTriggerClick();
            return;
        }
        if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
            event.preventDefault();
            if (!this.open) {
                this.show();
            }
            if (event.key === 'ArrowDown' && firstMenuItem) {
                menu.setCurrentItem(firstMenuItem);
                firstMenuItem.focus();
                return;
            }
            if (event.key === 'ArrowUp' && lastMenuItem) {
                menu.setCurrentItem(lastMenuItem);
                lastMenuItem.focus();
                return;
            }
        }
        const ignoredKeys = ['Tab', 'Shift', 'Meta', 'Ctrl', 'Alt'];
        if (this.open && menu && !ignoredKeys.includes(event.key)) {
            menu.typeToSelect(event.key);
        }
    }
    handlePanelSelect(event) {
        const target = event.target;
        if (target.tagName === 'ARC-MENU') {
            this.hide();
            this.focusOnTrigger();
        }
    }
    handleTriggerClick() {
        if (this.open) {
            this.hide();
        }
        else {
            this.show();
        }
    }
    updateAccessibleTrigger() {
        if (this.trigger) {
            const assignedElements = this.triggerSlot.assignedElements({ flatten: true });
            const accessibleTrigger = assignedElements.find(el => getTabbableBoundary(el).start);
            if (accessibleTrigger) {
                accessibleTrigger.setAttribute('aria-haspopup', 'true');
                accessibleTrigger.setAttribute('aria-expanded', this.open ? 'true' : 'false');
            }
        }
    }
    async show() {
        if (this.open) {
            return;
        }
        this.open = true;
        await waitForEvent(this, ARC_EVENTS.afterShow);
    }
    async hide() {
        if (!this.open) {
            return;
        }
        this.open = false;
        await waitForEvent(this, ARC_EVENTS.afterHide);
    }
    render() {
        return html `
      <div id="main">
        <span
          id="trigger"
          @click=${this.handleTriggerClick}
          @keydown=${this.handleTriggerKeyDown}
          @keyup=${this.handleTriggerKeyUp}
        >
          <slot id="triggerSlot" name="trigger" @slotchange=${this.updateAccessibleTrigger}></slot>
        </span>

        <!-- Position the panel with a wrapper since the popover makes use of translate. This lets us add animations
        on the panel without interfering with the position. -->
        <div id="positioner">
          <div
            id="panel"
            role="menu"
            aria-hidden=${this.open ? 'false' : 'true'}
            aria-labelledby="main"
            @arc-select=${this.handlePanelSelect}
          >
            <slot></slot>
          </div>
        </div>
      </div>
    `;
    }
}
ArcDropdown.tag = 'arc-dropdown';
ArcDropdown.styles = [
    componentStyles,
    css `
      :host {
        display: inline-flex;
        align-items: center;
      }

      #main,
      #trigger {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
      }

      #positioner {
        position: absolute;
        z-index: var(--arc-z-index-dropdown);
      }

      #panel {
        max-height: var(--arc-panel-height);
        min-width: var(--arc-panel-width);
        background-color: rgb(var(--arc-container-color));
        box-shadow: var(--arc-box-shadow);
        overflow: auto;
        overscroll-behavior: none;
        pointer-events: none;
      }

      :host([open]) #panel {
        pointer-events: all;
      }

      #positioner[data-popper-placement^='top'] #panel {
        transform-origin: bottom;
      }

      #positioner[data-popper-placement^='bottom'] #panel {
        transform-origin: top;
      }

      #positioner[data-popper-placement^='left'] #panel {
        transform-origin: right;
      }

      #positioner[data-popper-placement^='right'] #panel {
        transform-origin: left;
      }
    `,
];
__decorate([
    query('#trigger')
], ArcDropdown.prototype, "trigger", void 0);
__decorate([
    query('#triggerSlot')
], ArcDropdown.prototype, "triggerSlot", void 0);
__decorate([
    query('#panel')
], ArcDropdown.prototype, "panel", void 0);
__decorate([
    query('#positioner')
], ArcDropdown.prototype, "positioner", void 0);
__decorate([
    property({ type: String })
], ArcDropdown.prototype, "placement", void 0);
__decorate([
    property({ type: Number })
], ArcDropdown.prototype, "distance", void 0);
__decorate([
    property({ type: Number })
], ArcDropdown.prototype, "skidding", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ArcDropdown.prototype, "open", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ArcDropdown.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ArcDropdown.prototype, "hoist", void 0);
__decorate([
    watch('open', { waitUntilFirstUpdate: true })
], ArcDropdown.prototype, "handleOpenChange", null);
__decorate([
    watch('distance'),
    watch('hoist'),
    watch('placement'),
    watch('skidding')
], ArcDropdown.prototype, "handlePopoverOptionsChange", null);
setDefaultAnimation('dropdown.show', {
    keyframes: [
        { opacity: 0, transform: 'scale(0.9)' },
        { opacity: 1, transform: 'scale(1)' },
    ],
    options: { duration: 150, easing: 'ease' },
});
setDefaultAnimation('dropdown.hide', {
    keyframes: [
        { opacity: 1, transform: 'scale(1)' },
        { opacity: 0, transform: 'scale(0.9)' },
    ],
    options: { duration: 150, easing: 'ease' },
});
